import { useEffect, useState } from "react";
import { toast } from "sonner";

import ChevronDownIcon from "../../assets/svg/chevron-down.svg";
import {
  getOppositionHearingInvoicePaymentStatus,
  saveOppositionHearingInvoicePayment,
  type OppositionHearingInvoicePreview,
} from "../../api/opposition";

function CurrencyAmountField({
  label,
  value,
  onChange,
  readOnly = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  readOnly?: boolean;
}) {
  return (
    <div
      className="flex flex-col items-start gap-1"
      style={{ flex: "1 1 0", minWidth: 0 }}
    >
      <label
        className="flex items-center text-[12px] font-normal leading-5 text-[#222222]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        {label}
      </label>
      <div
        className="flex flex-row justify-between items-center w-full h-9 px-0 gap-2.5"
        style={{
          background: readOnly ? "#F7F7F7" : "#FFFFFF",
          border: "1px solid #EEEEEE",
          borderRadius: "6px",
          boxSizing: "border-box",
        }}
      >
        <input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) => {
            if (readOnly) return;
            const cleaned = e.target.value.replace(/[^\d.]/g, "");
            const parts = cleaned.split(".");
            const next =
              parts.length > 1
                ? `${parts[0]}.${parts.slice(1).join("")}`
                : cleaned;
            onChange(next);
          }}
          onKeyDown={(e) => {
            if (readOnly) return;
            if (
              e.key.length === 1 &&
              !e.ctrlKey &&
              !e.metaKey &&
              !/[\d.]/.test(e.key)
            ) {
              e.preventDefault();
            }
          }}
          readOnly={readOnly}
          placeholder="00.00"
          className="flex-1 min-w-0 h-full pl-2.5 pr-0 py-0 bg-transparent text-[12px] font-normal leading-5 text-[#222222] placeholder:text-[#6D6D6D] border-0 outline-none focus:ring-0 disabled:bg-transparent disabled:cursor-default"
          style={{ fontFamily: "Inter, sans-serif" }}
        />
        <div
          className="flex flex-row justify-center items-center shrink-0 px-2.5 py-2 gap-2.5 h-9 min-w-[76px]"
          style={{ background: "#F7F7F7", borderRadius: "0 6px 6px 0" }}
        >
          <span className="text-[12px] font-normal leading-5 text-[#6D6D6D]">Rs(₹)</span>
          <img src={ChevronDownIcon} alt="" className="w-4 h-4 shrink-0" aria-hidden />
        </div>
      </div>
    </div>
  );
}

function formatAmount(num: number | null | undefined): string {
  if (num == null || !Number.isFinite(num) || num === 0) return "";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function parseAmount(s: string): number {
  const cleaned = String(s || "").replace(/[^\d.]/g, "");
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export const OPPOSITION_PAYMENT_FOR_HEARING_FORM_ID =
  "opposition-payment-for-hearing-form";

export default function OppositionPaymentFromClientForHearingView({
  leadId,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [activeInvoice, setActiveInvoice] =
    useState<OppositionHearingInvoicePreview | null>(null);
  const [collectedAmount, setCollectedAmount] = useState<string>("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getOppositionHearingInvoicePaymentStatus(leadId)
      .then((invoices) => {
        if (cancelled) return;
        setActiveInvoice(invoices.find((inv) => inv.status === "ACTIVE") ?? null);
        setCollectedAmount("");
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load payment details"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const invoiceAmount = Number(activeInvoice?.invoiceAmount ?? 0);
  const savedCollected = Number(activeInvoice?.collectedAmount ?? 0);
  const thisPayment = parseAmount(collectedAmount);
  const totalCollected = savedCollected + thisPayment;
  const outstanding = Math.max(0, invoiceAmount - totalCollected);
  const outstandingFormatted = outstanding.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    if (!activeInvoice?.invoiceId) {
      toast.error("No active Hearing invoice. Save the invoice in Step 29 first.");
      return;
    }
    if (thisPayment <= 0) {
      toast.error("Collected amount must be greater than 0.");
      return;
    }
    if (totalCollected > invoiceAmount && invoiceAmount > 0) {
      toast.error("Collected amount cannot exceed the outstanding invoice balance.");
      return;
    }
    setSaving(true);
    try {
      const result = await saveOppositionHearingInvoicePayment(
        leadId,
        activeInvoice.invoiceId,
        { collectedAmount: thisPayment }
      );
      setActiveInvoice(result);
      setCollectedAmount("");
      toast.success("Payment recorded successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save payment");
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">Loading payment details…</p>
      </div>
    );
  }

  if (!activeInvoice) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          No active Hearing invoice. Save the invoice in Step 29 first.
        </p>
      </div>
    );
  }

  return (
    <form
      id={OPPOSITION_PAYMENT_FOR_HEARING_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col items-start w-full max-w-[1085px] gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {saving && <p className="text-xs text-[#6D6D6D]">Saving…</p>}
      <div className="flex flex-col items-start gap-[22px] w-full">
        <div className="flex flex-col sm:flex-row items-start sm:items-start gap-5 w-full">
          <CurrencyAmountField
            label="Invoice Amount for Hearing"
            value={formatAmount(invoiceAmount)}
            onChange={() => {}}
            readOnly
          />
          <div className="flex flex-col gap-1" style={{ flex: "1 1 0", minWidth: 0 }}>
            <CurrencyAmountField
              label="Collected Amount for Hearing"
              value={collectedAmount}
              onChange={setCollectedAmount}
            />
            {savedCollected > 0 && (
              <span className="text-[11px] leading-4 text-[#6D6D6D]">
                Already collected: Rs.{" "}
                {savedCollected.toLocaleString("en-IN", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                })}
                /-
              </span>
            )}
          </div>
        </div>
        <div
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-3 w-full px-4 sm:px-5 py-4 sm:py-0 min-h-[60px] sm:h-[60px]"
          style={{ background: "#222222", borderRadius: "8px" }}
        >
          <span className="text-[12px] sm:text-[14px] font-normal leading-5 text-white/90">
            OUTSTANDING AMOUNT FOR HEARING
          </span>
          <span className="text-[18px] sm:text-[22px] font-bold leading-8 sm:leading-10 text-white">
            Rs. {outstandingFormatted}/-
          </span>
        </div>
      </div>
    </form>
  );
}
